import { WebsocketMessage, WS_TOKEN_PARAM, WsReloadEventsFromServer } from "@dto/websocketMessage.dto";
import { useEffect, useState } from "react";
import { io, Socket } from "socket.io-client";
import { Log } from "src/service/log.service";
import { GLOB } from "src/util/Glob";

interface WsClient {
  count: number;
  /** Undefined until xormonReady resolves and connect() runs. */
  ws?: Socket<WsReloadEventsFromServer>;
  callbacks: Set<(data: WebsocketMessage) => void>;
  /** Notified when the socket comes back up; whatever was sent meanwhile is lost. */
  reconnectCallbacks: Set<() => void>;
  reconnects: number;
  connected?: boolean;
  /** When the current connection was established. */
  connectedAt?: number;
  /** Bounds retries of a connection the server keeps refusing. */
  serverDisconnects: number;
  error?: boolean;
  /** Pending report of a failed connect, cancelled if the socket comes up first. */
  connectErrorTimer?: ReturnType<typeof setTimeout>;
  /** When the tab last became visible, to keep the retry that follows a wake quiet. */
  wokeAt?: number;
}

const wsByNamespace: Record<string, WsClient> = {};

/** A woken tab needs a moment to get back on the network. */
const WAKE_GRACE_MS = 20_000;

/** Reconnecting after a thaw can take a while. */
const FREEZE_HINT_WINDOW_MS = 60_000;

/** How long a connection must last to count as accepted by the server. */
const STABLE_CONNECTION_MS = 30_000;

/** Retries of a server-ended socket, bounded so a rejected token cannot retry forever. */
const SERVER_DISCONNECT_RETRIES = 5;
const SERVER_DISCONNECT_RETRY_MS = 5_000;

/** A connect failure that recovers within this is not worth reporting. */
const CONNECT_ERROR_REPORT_DELAY_MS = 2_000;

/** Browsers freeze hidden tabs, so a drop while hidden is expected. */
function isExpectedDrop(wsc: WsClient) {
  return document.hidden || (wsc.wokeAt !== undefined && Date.now() - wsc.wokeAt < WAKE_GRACE_MS);
}

/** Shown once per session, since whitelisting is a one-off the user either does or ignores. */
const FREEZE_HINT_KEY = 'wsFreezeHintShown';

/** When the browser last thawed this tab, undefined if it never froze it. */
let thawedAt: number | undefined;

// socket.io's backoff would leave a woken tab dark for up to reconnectionDelayMax
function wakeSockets() {
  for (const wsc of Object.values(wsByNamespace)) {
    wsc.wokeAt = Date.now();
    wsc.ws?.connect(); // no-op when already connected
  }
}

document.addEventListener('visibilitychange', () => {
  if (!document.hidden) wakeSockets();
});

// only Chrome-style tab freezing fires this, which is the one case whitelisting fixes
document.addEventListener('resume', () => {
  thawedAt = Date.now();
  wakeSockets();
});

/** The browser caused the drop, so point at the setting that stops it. */
function hintAboutFreeze() {
  if (thawedAt === undefined || Date.now() - thawedAt > FREEZE_HINT_WINDOW_MS) return;
  if (sessionStorage.getItem(FREEZE_HINT_KEY)) return;
  sessionStorage.setItem(FREEZE_HINT_KEY, '1');
  Log.warn(<>
    The browser suspended this tab, so live updates were stopped.<br />
    To keep them running in the background:
    <ul>
      <li>allow this site to stay active<br />
        Chrome/Edge: Settings &rarr; Performance &rarr; Memory&nbsp;Saver &rarr; Always keep these sites active
      </li>
      <li>or pin the tab</li>
    </ul>
  </>, 10);
}

function connect(namespace: string, wsc: WsClient) {
  void GLOB.xormonReady.promise.then(() => {
    // entry was dropped or replaced while waiting, do not open an orphaned socket
    if (wsByNamespace[namespace] !== wsc || wsc.count <= 0 || wsc.ws) return;

    wsc.ws = io(GLOB.SOCKET_IO.url + namespace, {
      addTrailingSlash: false,
      path: GLOB.SOCKET_IO.path,
      // namespaces share one engine.io query; the callback re-reads a rotated token
      auth: cb => cb({ [WS_TOKEN_PARAM]: GLOB.userInfo?.wsToken }),
      reconnectionDelayMax: STABLE_CONNECTION_MS,
      withCredentials: true
    });

    const ws = wsc.ws;
    ws.on('connect', () => {
      const reconnected = wsc.connected === false;
      wsc.connected = true;
      wsc.connectedAt = Date.now();
      wsc.wokeAt = undefined;
      clearTimeout(wsc.connectErrorTimer);
      wsc.connectErrorTimer = undefined;
      if (wsc.error)
        Log.info(`WebSocket ${namespace} reconnected`);
      wsc.error = false;
      if (reconnected) {
        hintAboutFreeze();
        wsc.reconnects++;
        wsc.reconnectCallbacks.forEach(c => c());
      }
    });
    ws.on('disconnect', (reason) => {
      wsc.connected = false;
      // this reason unsubscribes the socket from its manager, so nothing else will retry it
      if (reason !== 'io server disconnect') return;
      // torn down by the last subscriber unmounting, or by an upgrade restarting the server
      if (wsByNamespace[namespace] !== wsc || GLOB.isUpgrading) return;
      // a connection the server kept proves the token works, so a later refusal starts over
      if (wsc.connectedAt !== undefined && Date.now() - wsc.connectedAt > STABLE_CONNECTION_MS)
        wsc.serverDisconnects = 0;
      if (wsc.serverDisconnects++ >= SERVER_DISCONNECT_RETRIES) {
        // nothing retries from here, so the page would go quietly stale
        Log.warn(`Live updates unavailable, the server refused the ${namespace} connection. Reload the page to retry.`);
        return;
      }
      setTimeout(() => {
        if (wsByNamespace[namespace] === wsc) wsc.ws?.connect();
      }, SERVER_DISCONNECT_RETRY_MS);
    });
    ws.on('connect_error', (err) => {
      if (wsc.error || wsc.connectErrorTimer !== undefined || GLOB.isUpgrading || isExpectedDrop(wsc)) return;
      wsc.connectErrorTimer = setTimeout(() => {
        wsc.connectErrorTimer = undefined;
        if (wsc.connected) return;
        wsc.error = true;
        Log.error(`WebSocket ${namespace} error`, err);
      }, CONNECT_ERROR_REPORT_DELAY_MS);
    });
    ws.on('message', msg => wsc.callbacks.forEach(c => c(msg)));
  }, console.debug);
}

export interface WsSubscription<T extends WebsocketMessage> {
  /** Last message received on the namespace. */
  message: T;
  /**
   * The last message of each event. Messages that arrive together (the server sends llm, grafana and license on
   * connect) are drawn in one render, where `message` holds only the last of them; a state read from one event
   * reads it here, so it is never lost.
   */
  lastByEvent: Partial<Record<string, T>>;
  /** Bumped on every reconnect: messages sent while the socket was down never arrived. */
  reconnects: number;
}

/** @param namespace Socket.IO namespace, all of them share one engine.io endpoint */
const useWebSocketSingleton = <T extends WebsocketMessage = WebsocketMessage>(namespace: string): WsSubscription<T> => {
  const [message, setMessage] = useState<T>();
  const [lastByEvent, setLastByEvent] = useState<Partial<Record<string, T>>>({});
  const [reconnects, setReconnects] = useState(0);

  useEffect(() => {
    // one socket serves every subscriber of a namespace, so the registry cannot be generic;
    // T only narrows what this caller expects out of the same payload
    const receive = (data: WebsocketMessage) => {
      setMessage(data as T);
      setLastByEvent((last) => ({ ...last, [data.event]: data as T }));
    };
    // claimed synchronously, or subscribers mounting in the same tick each open their own socket
    let wsc = wsByNamespace[namespace];
    const isFirstSubscriber = !wsc;
    if (!wsc)
      wsc = wsByNamespace[namespace] = { count: 0, callbacks: new Set(), reconnectCallbacks: new Set(), reconnects: 0, serverDisconnects: 0 };
    wsc.count++;
    wsc.callbacks.add(receive);
    const onReconnect = () => setReconnects(wsc.reconnects);
    wsc.reconnectCallbacks.add(onReconnect);
    if (isFirstSubscriber)
      connect(namespace, wsc);

    return () => {
      wsc.count--;
      wsc.callbacks.delete(receive);
      wsc.reconnectCallbacks.delete(onReconnect);
      if (wsc.count <= 0) {
        clearTimeout(wsc.connectErrorTimer);
        wsc.ws?.close();
        if (wsByNamespace[namespace] === wsc) delete wsByNamespace[namespace];
      }
    };
  }, []);

  return { message, lastByEvent, reconnects };
};

export default useWebSocketSingleton;
